import React, { useState } from 'react';
import styled from 'styled-components';
import { Paper, IconButton, Tooltip } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import FileCopyOutlined from '@material-ui/icons/FileCopyOutlined';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import NoteAddOutlined from '@material-ui/icons/NoteAddOutlined';
import OpenWithIcon from '@material-ui/icons/OpenWith';
import { ArrowUpward, ArrowDownward, ArrowBack, ArrowForward } from '@material-ui/icons';
import DeleteIcon from '@material-ui/icons/Delete';
import SubdirectoryArrowRightIcon from '@material-ui/icons/SubdirectoryArrowRight';
import { useDispatch } from 'react-redux';

import { addSheet, setDefaultSheet } from '../redux/sheetDbSlice';
import { addSheetInformation } from '../redux/sheetInformationDbSlice';
import { addCard } from '../redux/cardDbSlice';
import { addCardAttribute } from '../redux/cardAttributeDbSlice';
import { addCardShape } from '../redux/cardShapeDbSlice';
import { addMemoCardContent } from '../redux/memoCardContentDbSlice';
import { addUiCardItem } from '../redux/uiCardItemDbSlice';
import { addDrawCardContent } from '../redux/drawCardContentDbSlice';
import { addArrow } from '../redux/arrowDbSlice';
import { addSheetTree, updateSheetTree } from '../redux/sheetTreeDbSlice';

import useCurrentDb from '../hooks/useCurrentDb';
import useSheetTreeDbsByBook from '../hooks/useSheetTreeDbsByBook';
import useSheetsDb from '../hooks/useSheetsDb';
import useCurrentSheetDb from '../hooks/useCurrentSheetDb';
import useCurrentTreeHash from '../hooks/useCurrentTreeHash';
import useCopySheet from '../hooks/useCopySheet';
import useSheetTreeDbBySheet from '../hooks/useSheetTreeDbBySheet';

import { WorkAreaStateType } from './BookEditor';
import BookTree, { FoldedHash } from './BookTree';
import TreeMenu, { TreeMenuAnchorType, TreeMenuFunctionsType } from './TreeMenu';
import DeleteSheetDialog from './DeleteSheetDialog';
import { AddSheetRelation } from './AddSheetDialog';

const RightMenuPaper = styled(Paper)`
  box-sizing: border-box;
  height: 100%;
  border: 1px #eeeeee;
  border-style: none solid none none;
  background-color: inherit;
  overflow: auto;
`
const HeaderPaper = styled(Paper)`
  position: sticky;
  left: 0px;
  top: 0px;
  height: 48px;
  width: 100%;
  text-align: left;
  background-color: inherit;
  z-index: 1;
`
const BlueIconButton = styled(IconButton)`
  color: #1e88e5;
  background-color: inherit;
`

type RightMenuProps = {
  nodeClicked: (workArea: WorkAreaStateType, id?: string) => void
  workAreaState: WorkAreaStateType
  addSheetClicked: (relation: AddSheetRelation) => void
}
const RightMenu: React.FC<RightMenuProps> = ({nodeClicked, workAreaState, addSheetClicked}) => {
  type DialogActionState = {show: boolean};
  const [deleteSheetAction, setDeleteSheetAction] = useState<DialogActionState>({show: false});
  const dispatch = useDispatch();
  const currentSheet = useCurrentSheetDb();
  const [sheetId, bookId] = useCurrentDb();
  const [, currentSheetTree] = useSheetTreeDbBySheet(sheetId);
  const [sheetIds, sheets] = useSheetsDb(bookId);
  const [sheetTreeIds, sheetTrees] = useSheetTreeDbsByBook(bookId || undefined);
  const [folded, setFolded] = useState<FoldedHash>({});
  const sheetListValue = useCurrentTreeHash(bookId, folded);
  const [copyingSheetId, setCopyingSheetId] = useState<string>();
  const [recursiveCopy, setRecursiveCopy] = useState<boolean>(false);
  const copiedTree = useCopySheet(copyingSheetId, recursiveCopy);
  const duplicateTree = useCopySheet(sheetId || undefined, false);
  const duplicateRecursiveTree = useCopySheet(sheetId || undefined, true);
  const [menuAnchor, setMenuAnchor] = useState<TreeMenuAnchorType>();
  const [exCopyButton, setExCopyButton] = useState(false);
  const [exDuplicateButton, setExDuplicateButton] = useState(false);
  const [exMoveButton, setExMoveButton] = useState(false);

  const copyButtonId = 'rightmenu-sheet-copy-button';
  const duplicateButtonId = 'rightmenu-sheet-duplicate-button';
  const moveButtonId = 'rightmenu-sheet-move-button';

  const getExButtonPosition = (id: string) => {
    const anchorEl = document.getElementById(id);
    if (!anchorEl) return { left: 0, top: 0 };
    const left = anchorEl.getBoundingClientRect().left;
    const top = anchorEl.getBoundingClientRect().top - 18;
    return { left, top };
  }
  const copySheet = (recursive: boolean) => {
    console.log(sheetId)
    setCopyingSheetId(sheetId || undefined);
    setRecursiveCopy(recursive);
  }
  const pasteSheet = (orderAppoint?: boolean) => {
    console.log(copiedTree)
    if (!currentSheet || !copiedTree) return;
    // update order
    // if (orderAppoint) {
    //   sheetTreeIds.forEach((id, ix) => {
    //     const tree = sheetTrees[ix];
    //     if (tree.parentSheetId === currentSheetTree.parentSheetId && tree.order > currentSheetTree.order) dispatch(updateSheetTree({ ...tree, id, order: tree.order + 1 }));
    //   })
    // }
    // add new datas
    copiedTree.sheets.forEach(sheet => dispatch(addSheet(sheet)));
    copiedTree.sheetInformtions.forEach(sheetInfomation => dispatch(addSheetInformation(sheetInfomation)));
    copiedTree.sheetTrees.forEach((sheetTree, ix) => {
      if (ix === 0) {
        const { parentSheetId, ...value } = sheetTree;
        // const order = orderAppoint ? value.order : sheetTrees.filter(value => !value.parentSheetId).length;
        const order = sheetTrees.filter(value => !value.parentSheetId).length;
        // parentSheetIdを指定する、その場合のorderも調整必要
        dispatch(addSheetTree({ ...value, order }));
      }
      else dispatch(addSheetTree(sheetTree));
    });
    copiedTree.cards.forEach(card => dispatch(addCard(card)));
    copiedTree.cardAttributes.forEach(cardAttribute => dispatch(addCardAttribute(cardAttribute)));
    copiedTree.cardShapes.forEach(cardShape => dispatch(addCardShape(cardShape)));
    copiedTree.memoContents.forEach(memoContent => dispatch(addMemoCardContent(memoContent)));
    copiedTree.uiItems.forEach(uiItem => dispatch(addUiCardItem(uiItem)));
    copiedTree.drawContents.forEach(drawContent => dispatch(addDrawCardContent(drawContent)));
    copiedTree.arrows.forEach(arrow => dispatch(addArrow(arrow)));

    if (copiedTree.sheets[0].id) {
      nodeClicked('sheet', copiedTree.sheets[0].id);
      dispatch(setDefaultSheet(copiedTree.sheets[0].id));
    }
  }
  const duplicateSheet = (recursive: boolean) => {
    const usingTree = recursive ? duplicateRecursiveTree : duplicateTree;
    if (!currentSheet || !usingTree) return;
    // update order
    sheetTreeIds.forEach((id, ix) => {
      const tree = sheetTrees[ix];
      if (tree.parentSheetId === currentSheetTree.parentSheetId && tree.order > currentSheetTree.order) dispatch(updateSheetTree({ ...tree, id, order: tree.order + 1 }));
    })
    // add new datas
    usingTree.sheets.forEach(sheet => dispatch(addSheet(sheet)));
    usingTree.sheetInformtions.forEach(sheetInfomation => dispatch(addSheetInformation(sheetInfomation)));
    usingTree.sheetTrees.forEach(sheetTree => dispatch(addSheetTree(sheetTree)));
    usingTree.cards.forEach(card => dispatch(addCard(card)));
    usingTree.cardAttributes.forEach(cardAttribute => dispatch(addCardAttribute(cardAttribute)));
    usingTree.cardShapes.forEach(cardShape => dispatch(addCardShape(cardShape)));
    usingTree.memoContents.forEach(memoContent => dispatch(addMemoCardContent(memoContent)));
    usingTree.uiItems.forEach(uiItem => dispatch(addUiCardItem(uiItem)));
    usingTree.drawContents.forEach(drawContent => dispatch(addDrawCardContent(drawContent)));
    usingTree.arrows.forEach(arrow => dispatch(addArrow(arrow)));

    if (usingTree.sheets[0].id) {
      nodeClicked('sheet', usingTree.sheets[0].id);
      dispatch(setDefaultSheet(usingTree.sheets[0].id));
    }
  }
  const moveSheetUp = () => {
    if (!sheetId) return;
    const target = sheetListValue[sheetId];
    if (target.order === 0) return;
    let complete = false;
    for (const [id, value] of Object.entries(sheetListValue)) {
      if (id !== sheetId && value.parentId === target.parentId && value.order === target.order - 1) {
        dispatch(updateSheetTree({ id: value.treeId, order: value.order + 1 }));
        complete = true;
        break;
      }
    }
    if (complete) dispatch(updateSheetTree({ id: target.treeId, order: target.order - 1 }));
  }
  const moveSheetDown = () => {
    if (!sheetId) return;
    const target = sheetListValue[sheetId];
    let complete = false;
    for (const [id, value] of Object.entries(sheetListValue)) {
      if (id !== sheetId && value.parentId === target.parentId && value.order === target.order + 1) {
        dispatch(updateSheetTree({ id: value.treeId, order: value.order - 1 }));
        complete = true;
        break;
      }
    }
    if (complete) dispatch(updateSheetTree({ id: target.treeId, order: target.order + 1 }));
  }
  const getFunctionsDown = () => {
    if (!sheetId) return;
    const target = sheetListValue[sheetId];
    if (!target) return;
    for (const [, value] of Object.entries(sheetListValue)) {
      if (value && value.parentId === target.parentId && value.order > target.order) return moveSheetDown;
    }
  }
  const moveSheetLeft = () => {
    if (!sheetId) return;
    const target = sheetListValue[sheetId];
    const oldParent = sheetListValue[target.parentId];
    if (target.deepness === 0) return;
    for (const [id, value] of Object.entries(sheetListValue)) {
      if (id !== sheetId && value.parentId === target.parentId && value.order > target.order) {
        dispatch(updateSheetTree({ id: value.treeId, order: value.order - 1 }));
      } else if (value.parentId === oldParent.parentId && value.order > oldParent.order) {
        dispatch(updateSheetTree({ id: value.treeId, order: value.order + 1 }));
      }
    }
    const newParentId = oldParent.deepness === 0 ? undefined : oldParent.parentId;
    dispatch(updateSheetTree({ id: target.treeId, parentSheetId: newParentId, order: oldParent.order + 1 }));
  }
  const moveSheetRight = () => {
    if (!sheetId) return;
    const target = sheetListValue[sheetId];
    if (target.order === 0) return;
    const newParentData = Object.entries(sheetListValue).find(([, value]) => value.parentId === target.parentId && value.order === target.order - 1);
    if (!newParentData) return;
    let order = 0;
    for (const [id, value] of Object.entries(sheetListValue)) {
      if (id !== sheetId && value.parentId === target.parentId && value.order > target.order) {
        dispatch(updateSheetTree({ id: value.treeId, order: value.order - 1 }));
      } else if (value.parentId === newParentData[0]) order++;
    }
    dispatch(updateSheetTree({ id: target.treeId, parentSheetId: newParentData[0], order }));
  }

  const treeFunctions: TreeMenuFunctionsType = {
    add: (isNoParent) => addSheetClicked(isNoParent ? 'noParent' : 'brother'),
    addChild: () => addSheetClicked('child'),
    copy: () => copySheet(false),
    copyRecursive: () => copySheet(true),
    paste: copyingSheetId ? pasteSheet : undefined,
    duplicate: () => duplicateSheet(false),
    duplicateRecursive: () => duplicateSheet(true),
    up: currentSheetTree?.order >= 1 ? moveSheetUp : undefined,
    down: getFunctionsDown(),
    left: currentSheetTree?.parentSheetId ? moveSheetLeft : undefined,
    right: currentSheetTree?.order >= 1 ? moveSheetRight : undefined,
    delete: () => setDeleteSheetAction({ show: true })
  }

  return (
    <>
      <RightMenuPaper square className="ignore-on-screenshot tree-blank"
        onContextMenu={ev => {
          const target = ev.target as HTMLElement;
          if (typeof target.className === 'string' && target.className.includes('tree-blank')) setMenuAnchor({ open: true, anchorPosition: { left: ev.pageX, top: ev.pageY } });
        }}
      >
        <HeaderPaper square elevation={0}>
          <Tooltip title="同じ階層に新しいシートを追加する" placement="top-end">
            <BlueIconButton onClick={() => addSheetClicked('brother')}><AddIcon fontSize='small' /></BlueIconButton>
          </Tooltip>
          <Tooltip title="下の階層に新しいシートを追加する" placement="top-end">
            <BlueIconButton onClick={treeFunctions.addChild}><SubdirectoryArrowRightIcon fontSize='small' /><AddIcon fontSize='small' /></BlueIconButton>
          </Tooltip>
          <Tooltip title="このシートをコピーする" placement="top-end">
            <BlueIconButton id={copyButtonId} onMouseOver={() => setExCopyButton(true)} onMouseLeave={() => setExCopyButton(false)} onClick={treeFunctions.copy}><FileCopyOutlined fontSize='small' /></BlueIconButton>
          </Tooltip>
          <div style={{ position: 'absolute', ...getExButtonPosition(copyButtonId), visibility: exCopyButton ? 'visible': 'hidden', opacity: exCopyButton ? 1 : 0, transition: 'visibility 300ms, opacity 300ms' }}>
            <Tooltip title="階層ごとコピーする" placement="top-end">
              <BlueIconButton onMouseOver={() => setExCopyButton(true)} onMouseLeave={() => setExCopyButton(false)} onClick={treeFunctions.copyRecursive}><FileCopyOutlined fontSize='small' /></BlueIconButton>
            </Tooltip>
          </div>
          <Tooltip title="シートを貼り付ける" placement="top-end">
            <BlueIconButton onClick={() => copyingSheetId ? pasteSheet(true) : undefined} disabled={!treeFunctions.paste}><NoteAddOutlined fontSize='small' /></BlueIconButton>
          </Tooltip>
          <Tooltip title="このシートを複写する" placement="top-end">
            <BlueIconButton id={duplicateButtonId} onMouseOver={() => setExDuplicateButton(true)} onMouseLeave={() => setExDuplicateButton(false)} onClick={treeFunctions.duplicate}><FileCopyIcon fontSize='small' /></BlueIconButton>
          </Tooltip>
          <div style={{ position: 'absolute', ...getExButtonPosition(duplicateButtonId), visibility: exDuplicateButton ? 'visible': 'hidden', opacity: exDuplicateButton ? 1 : 0, transition: 'visibility 300ms, opacity 300ms' }}>
            <Tooltip title="階層ごと複写する" placement="top-end">
              <BlueIconButton onMouseOver={() => setExDuplicateButton(true)} onMouseLeave={() => setExDuplicateButton(false)} onClick={treeFunctions.duplicateRecursive}><FileCopyIcon fontSize='small' /></BlueIconButton>
            </Tooltip>
          </div>
          <Tooltip title="シートを移動する" placement="top-end">
          <BlueIconButton id={moveButtonId} onMouseOver={() => setExMoveButton(true)} onMouseLeave={() => setExMoveButton(false)}><OpenWithIcon fontSize='small' /></BlueIconButton>
          </Tooltip>
          <div style={{ position: 'absolute', ...getExButtonPosition(moveButtonId), width: 44, visibility: exMoveButton ? 'visible': 'hidden', opacity: exMoveButton ? 1 : 0, transition: 'visibility 600ms, opacity 600ms' }}>
            <Tooltip title="上に移動する" placement="top-end">
              <BlueIconButton onMouseOver={() => setExMoveButton(true)} onMouseLeave={() => setExMoveButton(false)} onClick={treeFunctions.up} disabled={!treeFunctions.up}><ArrowUpward fontSize='small' /></BlueIconButton>
            </Tooltip>
            <Tooltip title="下に移動する" placement="top-end">
              <BlueIconButton onMouseOver={() => setExMoveButton(true)} onMouseLeave={() => setExMoveButton(false)} onClick={treeFunctions.down} disabled={!treeFunctions.down}><ArrowDownward fontSize='small' /></BlueIconButton>
            </Tooltip>
            <Tooltip title="左に移動する" placement="top-end">
              <BlueIconButton onMouseOver={() => setExMoveButton(true)} onMouseLeave={() => setExMoveButton(false)} onClick={treeFunctions.left} disabled={!treeFunctions.left}><ArrowBack fontSize='small' /></BlueIconButton>
            </Tooltip>
            <Tooltip title="右に移動する" placement="top-end">
              <BlueIconButton onMouseOver={() => setExMoveButton(true)} onMouseLeave={() => setExMoveButton(false)} onClick={treeFunctions.right} disabled={!treeFunctions.right}><ArrowForward fontSize='small' /></BlueIconButton>
            </Tooltip>
          </div>
          <Tooltip title="このシートを削除する" placement="top-end">
            <BlueIconButton onClick={treeFunctions.delete}><DeleteIcon fontSize='small' /></BlueIconButton>
          </Tooltip>
        </HeaderPaper>
        <BookTree workAreaState={workAreaState}
          bookId={bookId} currentSheetId={sheetId}
          sheetIds={sheetIds} sheets={sheets}
          sheetTrees={sheetTrees}
          folded={folded} setFolded={setFolded}
          sheetListValue={sheetListValue}
          nodeClicked={nodeClicked}
          setMenuAnchor={setMenuAnchor}
        />
        <TreeMenu menuAnchor={menuAnchor}
          onClose={() => {
            setMenuAnchor(pre => {
              if (!pre) return undefined;
              return { ...pre, open: false };
            });
            setTimeout(() => setMenuAnchor(undefined), 280);
          }}
          functions={treeFunctions}
        />
      </RightMenuPaper>
      <DeleteSheetDialog open={deleteSheetAction.show} onClose={() => setDeleteSheetAction({show: false})}
        nodeClicked={nodeClicked} folded={folded}
      />
    </>
  )
}

export default RightMenu;
